import React, { useState } from 'react';
import { Loader2, PenLine, Type } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { SignaturePad } from './SignaturePad';
import { BusinessAssociateBlock } from './BaaSignatures';
import { baaApi, errorText } from './baaApi';

const FIELDS = [
  ['practiceName', 'Practice / Organization name', 'Bright Smiles Dental, PC'],
  ['practiceAddress', 'Practice address', '123 Main Street, City, ST 12345'],
  ['representativeName', 'Authorized representative (full name)', 'Jane Smith, DDS'],
  ['title', 'Title', 'Owner / Practice Administrator'],
  ['email', 'Email', 'you@practice.com'],
];

const SigToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-full bg-slate-100 p-1 text-sm">
    {[['typed', Type, 'Type'], ['drawn', PenLine, 'Draw']].map(([k, Icon, label]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`baa-signature-mode-${k}`}
        className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full transition-colors ${value === k ? 'bg-white shadow text-slate-900 font-semibold' : 'text-slate-500'}`}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </button>
    ))}
  </div>
);

export const BaaSignForm = ({ prefill, onSigned }) => {
  const [f, setF] = useState({ practiceName: '', practiceAddress: '', representativeName: '', title: '', email: '', ...prefill });
  const [mode, setMode] = useState('typed');
  const [typed, setTyped] = useState('');
  const [drawn, setDrawn] = useState('');
  const [agree, setAgree] = useState(false);
  const [templatesAck, setTemplatesAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const [tried, setTried] = useState(false);

  const sigOk = mode === 'typed' ? typed.trim().length >= 2 : !!drawn;
  const bad = {
    ...Object.fromEntries(FIELDS.map(([k]) => [k, !(f[k] || '').trim()])),
    email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((f.email || '').trim()),
    signature: !sigOk, agree: !agree, templatesAck: !templatesAck,
  };
  const missing = [...FIELDS.map(([k, label]) => [k, label]), ['signature', 'Signature'],
    ['agree', 'BAA agreement checkbox'], ['templatesAck', 'Templates & legal-review checkbox']].filter(([k]) => bad[k]);
  const red = (k) => (tried && bad[k] ? 'border-red-500 ring-2 ring-red-100' : '');

  const submit = async () => {
    if (missing.length) {
      setTried(true);
      setErr('');
      document.querySelector('[data-testid="baa-missing-alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => document.querySelector('[data-testid="baa-missing-alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const res = await baaApi.sign({ ...f, signatureType: mode, signatureText: typed, signatureImage: mode === 'drawn' ? drawn : null, agree, templatesAcknowledged: templatesAck });
      onSigned(res.agreement);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="baa-sign-form">
      <p className="text-[11px] font-bold tracking-[0.16em] text-slate-500 uppercase">Covered Entity · Electronic Acceptance</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, ph]) => (
          <div key={k} className={k === 'practiceAddress' ? 'sm:col-span-2' : ''}>
            <Label htmlFor={`baa-${k}`} className="text-xs text-slate-600">{label}</Label>
            <Input id={`baa-${k}`} data-testid={`baa-input-${k}`} value={f[k] || ''} placeholder={ph}
              type={k === 'email' ? 'email' : 'text'} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`mt-1 ${red(k)}`} />
            {tried && bad[k] && <p className="text-xs text-red-600 mt-1">{k === 'email' && (f.email || '').trim() ? 'Enter a valid email address' : 'Required'}</p>}
          </div>
        ))}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-slate-600">Signature</Label>
          <SigToggle value={mode} onChange={setMode} />
        </div>
        {mode === 'typed' ? (
          <div>
            <Input data-testid="baa-input-signature" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type your full name" className={red('signature')} />
            {typed && <p className="text-3xl text-[#0b2545] mt-2 px-1" style={{ fontFamily: '"Dancing Script", "Brush Script MT", cursive' }}>{typed}</p>}
          </div>
        ) : (
          <div className={tried && bad.signature ? 'rounded-lg ring-2 ring-red-500' : ''}><SignaturePad onChange={setDrawn} /></div>
        )}
        {tried && bad.signature && <p className="text-xs text-red-600 mt-1">Signature required</p>}
      </div>
      <label className={`flex items-start gap-3 rounded-xl border bg-slate-50 p-4 cursor-pointer ${tried && bad.agree ? 'border-red-500 ring-2 ring-red-100' : 'border-slate-200'}`}>
        <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="baa-agree-checkbox" className="mt-0.5" />
        <span className="text-sm text-slate-700">
          I have read and agree to the PractCom Business Associate Agreement and represent that I am authorized to accept it on behalf of the dental practice or organization identified above.
        </span>
      </label>
      <label className={`flex items-start gap-3 rounded-xl border bg-slate-50 p-4 cursor-pointer ${tried && bad.templatesAck ? 'border-red-500 ring-2 ring-red-100' : 'border-slate-200'}`}>
        <Checkbox checked={templatesAck} onCheckedChange={(v) => setTemplatesAck(!!v)} data-testid="baa-templates-checkbox" className="mt-0.5" />
        <span className="text-sm text-slate-700">
          I understand that PractCom is a communication tool, not a legal advisor or compliance service. All consent forms, medical alert cards, management forms, referral letters, procedure explanations and other documents provided by PractCom are templates that my practice is responsible for customizing and having reviewed by our own legal counsel before use with any patient.
        </span>
      </label>
      <BusinessAssociateBlock />
      <p className="text-xs text-slate-500">We record the date, time, IP address, BAA version and Terms version with your acceptance.</p>
      {tried && missing.length > 0 && (
        <div role="alert" data-testid="baa-missing-alert" className="rounded-xl border-2 border-red-500 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-bold">Please complete all required fields before signing:</p>
          <ul className="list-disc pl-5 mt-1">{missing.map(([k, label]) => <li key={k}>{label}</li>)}</ul>
        </div>
      )}
      {err && <p className="text-sm text-red-600" data-testid="baa-sign-error">{err}</p>}
      <Button onClick={submit} disabled={busy} data-testid="baa-sign-submit"
        className="w-full h-12 rounded-full bg-[#0b2545] hover:bg-[#13315c] text-white font-semibold">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'I Agree, Sign the Business Associate Agreement'}
      </Button>
    </div>
  );
};
